/**
 * sinteseStore — Gerencia sínteses por tag/projeto com versionamento (snapshots estilo Scrivener)
 *
 * Consumidores: SinteseArea, ArticulacaoModal, ConexoesInspector, Conexoes
 * Dependências: nenhuma
 * Persistência: localStorage (bigwa-sinteses) + Google Drive (project JSON)
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SinteseVersao, BlocoSintese } from '../types/sintese';
import { googleDrive } from '../services/googleDrive';
import { logger } from '../utils/logger';
import { bigwaToast } from '../utils/bigwaToast';

// Debounced Drive save — coalesces rapid mutations into a single save per project
let _sinteseSaveTimer: ReturnType<typeof setTimeout> | null = null;
let _sinteseRetryInitialized = false;
// Guard de sequência para o clear-on-success: cada agendamento de save (1× por mutação,
// já que só as mutações chamam esta função) incrementa _saveSeq[projectId]. O save captura
// o valor no início e, no sucesso, só limpa dirty se o valor não mudou — nenhuma mutação nova
// entrou desde o início daquele save. Impede que um sucesso atrasado apague um dirty que uma
// edição posterior re-marcou (AC-4 / contrato #4, Sprint 043 1c).
const _saveSeq: Record<string, number> = {};

// Supressor do toast F4: 1× por episódio de falha de save, keyed em projectId (Sprint 043 1e).
const _errorToastShown: Record<string, boolean> = {};

// Salva sinteses[projectId] ao Drive por-projeto — reusável por debounce/retry(F2)/flush → R5.
// Carrega o clear-on-success com guard de sequência (1c): no sucesso, só limpa dirty[projectId]
// se nenhuma mutação nova bumpou _saveSeq[projectId] desde o início deste save.
function _saveProjectToDrive(projectId: string) {
  const seqAtSaveStart = _saveSeq[projectId] || 0;
  const projectSinteses = useSinteseStore.getState().sinteses[projectId] || {};
  const json = JSON.stringify(projectSinteses);
  return googleDrive.saveSinteses(projectId, json).then(() => {
    // clear-on-success com guard de sequência: só limpa se nenhuma mutação nova entrou
    if ((_saveSeq[projectId] || 0) === seqAtSaveStart) {
      useSinteseStore.setState((s) => ({ dirty: { ...s.dirty, [projectId]: false } }));
      _errorToastShown[projectId] = false; // fim do episódio: rearma o toast F4 (1e)
    }
  }).catch(err => {
    logger.error('[sinteseStore] Drive save failed:', err);
    // Toast F4: 1× por episódio de falha (supressor keyed em projectId; rearmado na borda
    // dirty 0→1 e no clear-on-success). O invariante "1×" mora aqui, não no de-dup do toast (1e).
    if (!_errorToastShown[projectId]) {
      bigwaToast.error(
        'Não foi possível salvar as sínteses no Drive. Suas edições estão guardadas e serão sincronizadas automaticamente.',
        { id: `sintese-save-error:${projectId}` },
      );
      _errorToastShown[projectId] = true;
    }
  });
}

// Marca o projeto dirty e agenda o save debounced. Na borda 0→1 (início de um novo
// episódio de edição), rearma o supressor do toast F4 para que uma nova falha volte a
// notificar 1× (Sprint 043 1e).
function _markDirtyAndSchedule(projectId: string) {
  const wasDirty = !!useSinteseStore.getState().dirty[projectId];
  useSinteseStore.setState((s) => ({ dirty: { ...s.dirty, [projectId]: true } }));
  if (!wasDirty) _errorToastShown[projectId] = false; // borda 0→1: novo episódio
  _scheduleSinteseSaveToDrive();
}

function _scheduleSinteseSaveToDrive() {
  if (_sinteseSaveTimer) clearTimeout(_sinteseSaveTimer);
  const pendingProjectId = useSinteseStore.getState().currentProjectId;
  if (pendingProjectId) _saveSeq[pendingProjectId] = (_saveSeq[pendingProjectId] || 0) + 1;
  _sinteseSaveTimer = setTimeout(() => {
    _sinteseSaveTimer = null;
    const projectId = useSinteseStore.getState().currentProjectId;
    if (!projectId) return;
    _saveProjectToDrive(projectId);
  }, 3000);
}

// Retry F2: ao voltar `online`, reenfileira o save de CADA projeto dirty (não só o corrente — R5).
function _handleSinteseOnline() {
  const dirty = useSinteseStore.getState().dirty;
  Object.keys(dirty).filter((projectId) => dirty[projectId]).forEach((projectId) => {
    _saveProjectToDrive(projectId);
  });
}

/**
 * Inicializa o retry de sínteses no evento `online` (F2). Espelha offlineDetection:
 * guarda _sinteseRetryInitialized + listener próprio, independente do handleOnline de
 * offlineDetection (que não expõe subscribe). Wired uma vez no boot a partir de main.tsx.
 */
export function initSinteseOnlineRetry() {
  if (_sinteseRetryInitialized) return;
  window.addEventListener('online', _handleSinteseOnline);
  _sinteseRetryInitialized = true;
}

/**
 * Remove o listener de retry `online` (espelho de cleanupOfflineDetection).
 */
export function cleanupSinteseOnlineRetry() {
  if (!_sinteseRetryInitialized) return;
  window.removeEventListener('online', _handleSinteseOnline);
  _sinteseRetryInitialized = false;
}

/**
 * Flush F3 no teardown (defesa em profundidade — Pilar 1 / AC-7). Wired em main.tsx a
 * visibilitychange(hidden) (primário — o único confiável no mobile, onde beforeunload
 * frequentemente nem fira) + pagehide (backstop terminal).
 *
 * R1 — flush = "cancelar o timer e disparar uma vez", não "salvar ao esconder": cancela o
 * debounce pendente (clearTimeout, como flushDestinoSync) para não gerar um 2º PATCH, e só
 * salva se há edição pendente (dirty) — nada dirty ⇒ zero PATCH, mantendo os PATCHes
 * proporcionais às trocas de aba (Pilar 4).
 * R2 — idempotência: após o clear-on-success limpar dirty, um 2º flush encontra nada e no-opa;
 * o vai-e-volta de aba não gera PATCH redundante.
 * R5 — cobre TODOS os projetos dirty (não só o corrente), espelhando o retry F2: itera
 * Object.keys(dirty).filter(...) e salva cada um com seu próprio sinteses[projectId].
 * O PATCH concorrente com o debounce já nasce serializado pelo single-flight do repository (D2).
 */
export function flushSinteses() {
  if (_sinteseSaveTimer) {
    clearTimeout(_sinteseSaveTimer);
    _sinteseSaveTimer = null;
  }
  const dirty = useSinteseStore.getState().dirty;
  Object.keys(dirty).filter((projectId) => dirty[projectId]).forEach((projectId) => {
    _saveProjectToDrive(projectId);
  });
}

interface SinteseState {
  sinteses: Record<string, Record<string, BlocoSintese>>; // projectId -> tag -> bloco
  isLoading: boolean;
  currentProjectId: string | null;
  sinteseLoadedAt: number | null;
  dirty: Record<string, boolean>; // projectId → edição local não-durada

  setCurrentProject: (projectId: string) => void;
  addVersao: (tag: string, conteudo: string, origem: 'ia' | 'usuario' | 'editada', citacoesIds: string[], titulo?: string) => void;
  updateVersao: (tag: string, versaoId: string, conteudo: string) => void;
  deleteVersao: (tag: string, versaoId: string) => void;
  setVersaoAtual: (tag: string, versaoNum: number) => void;
  renameVersao: (tag: string, versaoId: string, titulo: string) => void; // ✅ NOVO
  getSintese: (tag: string) => BlocoSintese | null;
  getVersaoAtual: (tag: string) => SinteseVersao | null;
  getAllSinteses: () => BlocoSintese[];
  clearProjectData: () => void;
  loadFromDrive: (projectId: string) => Promise<void>;
  revalidateIfStale: () => void;
}

export const useSinteseStore = create<SinteseState>()(
  persist(
    (set, get) => ({
      sinteses: {},
      isLoading: false,
      currentProjectId: null,
      sinteseLoadedAt: null,
      dirty: {},

      clearProjectData: () => {
        set({ currentProjectId: null });
      },

      setCurrentProject: (projectId: string) => {
        set({ currentProjectId: projectId });
        // Inicializa estrutura se não existir
        const { sinteses } = get();
        if (!sinteses[projectId]) {
          set({ sinteses: { ...sinteses, [projectId]: {} } });
        }
        // Load from Drive (fire-and-forget — localStorage already has cached data via persist)
        get().loadFromDrive(projectId);
      },
      
      addVersao: (tag, conteudo, origem, citacoesIds, titulo) => {
        const { sinteses, currentProjectId } = get();
        if (!currentProjectId) return;
        
        const tagKey = tag.toLowerCase();
        const projectSinteses = sinteses[currentProjectId] || {};
        const blocoExistente = projectSinteses[tagKey];
        const versoes = blocoExistente?.versoes || [];
        const proximaVersao = versoes.length + 1;
        
        const novaVersao: SinteseVersao = {
          id: crypto.randomUUID(),
          versao: proximaVersao,
          conteudo,
          origem,
          citacoesUsadas: citacoesIds.length,
          citacoesIds,
          criadoEm: new Date().toISOString(),
          titulo: titulo || undefined // ✅ NOVO: título opcional
        };
        
        const blocoAtualizado: BlocoSintese = {
          tag: tagKey,
          versaoAtual: proximaVersao,
          versoes: [...versoes, novaVersao],
          ultimaAtualizacao: new Date().toISOString()
        };
        
        set({
          sinteses: {
            ...sinteses,
            [currentProjectId]: {
              ...projectSinteses,
              [tagKey]: blocoAtualizado
            }
          }
        });
        _markDirtyAndSchedule(currentProjectId);
      },

      updateVersao: (tag, versaoId, conteudo) => {
        const { sinteses, currentProjectId } = get();
        if (!currentProjectId) return;
        
        const tagKey = tag.toLowerCase();
        const projectSinteses = sinteses[currentProjectId] || {};
        const bloco = projectSinteses[tagKey];
        if (!bloco) return;
        
        const versoesAtualizadas = bloco.versoes.map(v => 
          v.id === versaoId 
            ? { ...v, conteudo, origem: 'editada' as const, editadoEm: new Date().toISOString() } 
            : v
        );
        
        set({
          sinteses: {
            ...sinteses,
            [currentProjectId]: {
              ...projectSinteses,
              [tagKey]: {
                ...bloco,
                versoes: versoesAtualizadas,
                ultimaAtualizacao: new Date().toISOString()
              }
            }
          }
        });
        _markDirtyAndSchedule(currentProjectId);
      },

      // ✅ NOVO: Renomear snapshot (estilo Scrivener)
      renameVersao: (tag, versaoId, titulo) => {
        const { sinteses, currentProjectId } = get();
        if (!currentProjectId) return;
        
        const tagKey = tag.toLowerCase();
        const projectSinteses = sinteses[currentProjectId] || {};
        const bloco = projectSinteses[tagKey];
        if (!bloco) return;
        
        const versoesAtualizadas = bloco.versoes.map(v => 
          v.id === versaoId 
            ? { ...v, titulo: titulo.trim() || undefined } 
            : v
        );
        
        set({
          sinteses: {
            ...sinteses,
            [currentProjectId]: {
              ...projectSinteses,
              [tagKey]: {
                ...bloco,
                versoes: versoesAtualizadas,
                ultimaAtualizacao: new Date().toISOString()
              }
            }
          }
        });
        _markDirtyAndSchedule(currentProjectId);
      },

      deleteVersao: (tag, versaoId) => {
        const { sinteses, currentProjectId } = get();
        if (!currentProjectId) return;
        
        const tagKey = tag.toLowerCase();
        const projectSinteses = sinteses[currentProjectId] || {};
        const bloco = projectSinteses[tagKey];
        if (!bloco || bloco.versoes.length <= 1) return;
        
        const versoesFiltradas = bloco.versoes.filter(v => v.id !== versaoId);
        const versaoAtual = Math.max(...versoesFiltradas.map(v => v.versao));
        
        set({
          sinteses: {
            ...sinteses,
            [currentProjectId]: {
              ...projectSinteses,
              [tagKey]: {
                ...bloco,
                versaoAtual,
                versoes: versoesFiltradas,
                ultimaAtualizacao: new Date().toISOString()
              }
            }
          }
        });
        _markDirtyAndSchedule(currentProjectId);
      },

      setVersaoAtual: (tag, versaoNum) => {
        const { sinteses, currentProjectId } = get();
        if (!currentProjectId) return;
        
        const tagKey = tag.toLowerCase();
        const projectSinteses = sinteses[currentProjectId] || {};
        const bloco = projectSinteses[tagKey];
        if (!bloco) return;
        
        set({
          sinteses: {
            ...sinteses,
            [currentProjectId]: {
              ...projectSinteses,
              [tagKey]: {
                ...bloco,
                versaoAtual: versaoNum,
                ultimaAtualizacao: new Date().toISOString()
              }
            }
          }
        });
        _markDirtyAndSchedule(currentProjectId);
      },

      getSintese: (tag) => {
        const { sinteses, currentProjectId } = get();
        if (!currentProjectId) return null;
        const projectSinteses = sinteses[currentProjectId] || {};
        return projectSinteses[tag.toLowerCase()] || null;
      },
      
      getVersaoAtual: (tag) => {
        const bloco = get().getSintese(tag);
        if (!bloco || bloco.versoes.length === 0) return null;
        return bloco.versoes.find(v => v.versao === bloco.versaoAtual) || null;
      },
      
      getAllSinteses: () => {
        const { sinteses, currentProjectId } = get();
        if (!currentProjectId) return [];
        const projectSinteses = sinteses[currentProjectId] || {};
        return Object.values(projectSinteses);
      },

      loadFromDrive: async (projectId: string) => {
        try {
          const driveData = await googleDrive.loadSinteses(projectId);
          if (driveData) {
            const parsed = JSON.parse(driveData) as Record<string, BlocoSintese>;
            // Only apply if Drive has data (don't overwrite local with empty)
            if (Object.keys(parsed).length > 0) {
              const { sinteses, dirty } = get();
              if (dirty[projectId]) {
                // Edição local não-durada — preserva; Drive stale NÃO sobrescreve (I3/AC-3)
                set({ sinteseLoadedAt: Date.now() });
                return;
              }
              set({
                sinteses: {
                  ...sinteses,
                  [projectId]: parsed
                },
                sinteseLoadedAt: Date.now()
              });
              return;
            }
          }
          // Drive had no data — keep localStorage state, just mark as loaded
          set({ sinteseLoadedAt: Date.now() });
        } catch (err) {
          logger.error('[sinteseStore] Failed to load from Drive:', err);
          set({ sinteseLoadedAt: Date.now() });
        }
      },

      revalidateIfStale: () => {
        const { sinteseLoadedAt, isLoading, currentProjectId, dirty } = get();
        if (!sinteseLoadedAt || isLoading || !currentProjectId) return;
        if (Date.now() - sinteseLoadedAt < 300_000) return; // 5min TTL
        if (dirty[currentProjectId]) return; // há edição local a durar primeiro
        get().loadFromDrive(currentProjectId);
      }
    }),
    {
      name: 'bigwa-sinteses',
      partialize: (state) => ({ sinteses: state.sinteses, dirty: state.dirty })
    }
  )
);
